<template>
  <div class="p-3 bg-white">
    <!-- select -->
    <b-row no-gutters class="mb-3">
      <b-col>
        <div class="d-flex flex-row">
          <div class="w-25">
            <simple-select
              :select-size="'sm'"
              :class="['bg-white']"
              :options="options"
              :selected="getSelectedWeightType"
              @emit-selected="onChangeWeights"
            ></simple-select>
          </div>
          <div class="d-inline ml-3 p-1" v-if="getSelectedScoringDefinition.guideline">
            <span class="text-muted align-middle">
              {{ $t('scoringProfiles.customDriverGuideline') }}
            </span>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- slider -->
    <b-row no-gutters v-if="getSelectedScoringDefinition.slider">
      <b-col>
        <div class="mb-1">
          <custom-slider
            :value.sync="geometricDriversSequenceWeightsDecay"
            :min="0"
            :max="1"
            :interval="0.01"
            :process="false"
            :tooltip="'none'"
            :direction="'rtl'"
          ></custom-slider>
        </div>
        <div class="d-flex flex-row">
          <div>
            <span class="slider-bottom-label">{{ $t('scoringProfiles.label.equallyWeighted') }}</span>
            <custom-tooltip
              :index="0"
              :css-class="['ml-2 pointer']"
              :section="'scoring-definition'"
              :tooltipText="$t('scoringProfilesModelEdit.tips.equallyWeighted')"
            ></custom-tooltip>
          </div>
          <div class="ml-auto">
            <span class="slider-bottom-label">{{ $t('scoringProfiles.label.skewed') }}</span>
            <custom-tooltip
              :index="1"
              :css-class="['ml-2 pointer']"
              :section="'scoring-definition'"
              :tooltipText="$t('scoringProfilesModelEdit.tips.skewed')"
            ></custom-tooltip>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- separator -->
    <b-row no-gutters>
      <b-col>
        <hr />
      </b-col>
    </b-row>

    <!-- validation messages -->
    <template v-if="getSelectedWeightType === 2">
      <b-row
        no-gutters
        :class="getDriversWeightsGreaterThanZero ? 'mb-3' : 'mb-0'"
        v-if="getSelectedScoringDefinition.list.length > 0 && !isValidMaxTotalWeight"
      >
        <b-col>
          <div class="text-danger">
            <font-awesome-icon class="small" icon="exclamation-triangle" />
            <span>{{ $t('scoringProfilesModelEdit.currentWeightDriver') }}: </span>
            <span class="font-weight-bold">{{ $n(getMaxTotalWeight, 'percent') }}.</span>
            <span class="ml-1" v-html="$t('scoringProfiles.maxWeight')"></span>
          </div>
        </b-col>
      </b-row>

      <b-row
        no-gutters
        class="mb-3"
        v-if="getSelectedScoringDefinition.list.length > 0 && !getDriversWeightsGreaterThanZero"
      >
        <b-col>
          <div class="text-danger">
            <font-awesome-icon class="small mr-1" icon="exclamation-circle" />
            <span v-html="$t('scoringProfilesModelEdit.weightsMustBeGreaterThan0Driver')"></span>
          </div>
        </b-col>
      </b-row>
    </template>

    <!-- scoring definition component -->
    <b-row no-gutters class="mb-3">
      <b-col>
        <scoring-profiles-definition
          :is-modeling="true"
          :indicators="{}"
          :drivers-limit="30"
          :drivers="getSelectedScoringDefinition.list"
          :weight-type="getSelectedScoringDefinition.config"
          :can-edit-badges="getSelectedScoringDefinition.edit"
          :sequence-weights-decay="getSequenceWeightsDecay"
          :indicators-categories="[]"
          @on-update-custom-driver-weight="onUpdateCustomDriverWeight"
        ></scoring-profiles-definition>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import SimpleSelect from '../../components/Lib/SimpleSelect.vue'
import CustomTooltip from '../../components/Lib/CustomTooltip.vue'
import CustomSlider from '../../components/Lib/CustomSlider.vue'
import ScoringProfilesDefinition from '../../components/ScoringProfiles/ScoringProfilesDefinition.vue'
import { calculateDriversWeightsLists, formatDriverWeightNumber } from '../../utils/drivers'
import { SCORING_DEFINITION_TYPES } from '../../settings/scoringProfilesSettings'
import { isNearlyEqual } from '../../utils/formatter'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapMutations } = createNamespacedHelpers('scoringProfilesDefinition')

export default {
  components: {
    SimpleSelect,
    CustomTooltip,
    CustomSlider,
    ScoringProfilesDefinition,
  },

  computed: {
    ...mapGetters([
      'getMaxTotalWeight',
      'getSelectedWeightType',
      'getSequenceWeightsDecay',
      'getSelectedScoringDefinition',
      'getScoringDefinitionSettings',
      'getDriversWeightsGreaterThanZero',
    ]),

    options: {
      get() {
        return [
          { value: 1, text: this.$t('scoringProfiles.geometricOption') },
          { value: 2, text: this.$t('scoringProfiles.customOption') },
          { value: 3, text: this.$t('scoringProfiles.linearOption') },
        ]
      },
    },

    geometricDriversSequenceWeightsDecay: {
      get() {
        return this.getSequenceWeightsDecay
      },
      set(newVal) {
        this.setSequenceWeightsDecay(newVal)
        let list = [...this.getSelectedScoringDefinition.list]
        calculateDriversWeightsLists(
          null,
          list,
          this.getSelectedScoringDefinition.config,
          this.getSequenceWeightsDecay,
          this.setDriversList
        )
      },
    },

    isValidMaxTotalWeight: {
      get() {
        const maxWeight = this.getMaxTotalWeight
        const epsilon = this.getSelectedScoringDefinition.config === SCORING_DEFINITION_TYPES.custom ? 0.0001 : 0.0005
        return isNearlyEqual(maxWeight, 1, epsilon)
      },
    },
  },

  methods: {
    ...mapMutations(['setSelectedWeightType', 'setSequenceWeightsDecay', 'setDriversList']),

    onChangeWeights(val) {
      let sourceList = this.getSelectedScoringDefinition.list
      let targetList = this.getScoringDefinitionSettings[val].list

      if (this.getScoringDefinitionSettings[val].config === SCORING_DEFINITION_TYPES.custom && targetList.length > 0) {
        sourceList.forEach((j) => {
          const el = targetList.find((k) => (k.driver || k.family) === (j.driver || j.family))
          j.weight = el ? el.weight : j.weight
        })
      }
      this.setSelectedWeightType(val)

      calculateDriversWeightsLists(
        null,
        sourceList,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onUpdateCustomDriverWeight(family, value) {
      let list = [...this.getSelectedScoringDefinition.list]
      const index = list.findIndex((el) => el.family === family)
      list[index].weight = formatDriverWeightNumber(value)
      this.setDriversList(list)
    },
  },
}
</script>
